/**
 * The registration interface for markers — the one thing the module hands back to the app.
 *
 * ```tsx
 * const { isNew, markUsed, spotlight, ref } = useWhatsNewAnchor<HTMLButtonElement>(
 *     'tab:lost-business',
 *     { reveal: () => setActiveTab('lostbusiness') },
 * )
 *
 * <Tab ref={ref} onClick={markUsed} className={spotlight ? SPOTLIGHT_CLASS : ''}>
 *     Lost Business {isNew && <NewMarker />}
 * </Tab>
 * ```
 *
 * The id is whatever the app says it is. `tab:`, `button:`, `field:` are conventions of the
 * host, not of the module: nothing here parses an id, and nothing here knows what a tab is.
 * That is D6 in one file.
 *
 * ## `reveal` — how "Show me" opens a place the module has never heard of
 *
 * The panel's "Show me" has to put the reader in front of the thing. Only the app can do that:
 * it might be a tab switch, a scroll, an accordion, a route. So the app registers HOW, the
 * module decides WHEN, and the module still knows nothing. An anchor with no `reveal` simply
 * gets no "Show me" button — better than a button that does nothing.
 *
 * Since DU6 the wrapper also moves **focus** to the element afterwards, so "Show me" serves a
 * keyboard the way it serves an eye (S2-24). That is the whole reason the hook hands back a
 * `ref`: the module cannot know which node the host called its anchor, and a focus that lands
 * nowhere would leave the reader on the button of a panel that has just closed.
 *
 * `reveal` is held in a ref and read through a stable wrapper. An inline arrow function as a
 * dependency would re-register on every render, and since registering re-renders the provider,
 * that is an endless loop rather than a slow app. It cost one run to find; the ref is the fix.
 *
 * ## `markUsed` is called on USE, not on sight
 *
 * `onClick`, not `useEffect`. A marker that cleared itself when it scrolled into view would be
 * gone before the reader reached it — which is the failure this whole marker level exists to
 * avoid (D1).
 */

import { useEffect, useRef, type RefObject } from 'react';
import { useWhatsNew } from './WhatsNewProvider';

export interface WhatsNewAnchor<T extends HTMLElement = HTMLElement> {
    /** Show a marker here. False until the state has loaded, and after a failure (D9). */
    isNew: boolean;
    /** Call when the element is actually used. Idempotent, optimistic, survives a reload (D3). */
    markUsed: () => void;
    /** True for a couple of seconds after "Show me" pointed here. Pair with `SPOTLIGHT_CLASS`. */
    spotlight: boolean;
    /**
     * Put this on the element itself, and "Show me" moves the keyboard there as well as the
     * eye — S2-24 of the design audit, DU6. Optional: an anchor whose ref is never attached
     * still reveals and still spotlights, it just leaves focus where the panel put it back.
     */
    ref: RefObject<T>;
}

export interface WhatsNewAnchorOptions {
    /** How to put this element in front of the reader. Omit it and "Show me" is not offered. */
    reveal?: () => void;
}

export function useWhatsNewAnchor<T extends HTMLElement = HTMLElement>(
    id: string,
    options?: WhatsNewAnchorOptions,
): WhatsNewAnchor<T> {
    const { isAnchorActive, markAnchorUsed, registerAnchor, isSpotlit } = useWhatsNew();

    const reveal = useRef(options?.reveal);
    reveal.current = options?.reveal;

    /** The element, if the host attached the ref. Only the focus hand-off reads it. */
    const element = useRef<T>(null);

    // The WRAPPER must be absent when there is no `reveal`, not merely call nothing: the panel
    // decides whether to offer "Show me" by asking whether this anchor can be revealed, and a
    // wrapper that is always truthy made it offer a button that did nothing — the opposite of
    // what the paragraph above promises (`/code-review high`, W2).
    const canReveal = !!options?.reveal;
    useEffect(
        () => registerAnchor(id, canReveal ? () => {
            reveal.current?.();
            // **Two frames, and both are load-bearing.** `reveal` is a state change in the
            // host — a tab switch — so the element may not exist yet when this returns; one
            // frame gets it rendered. The panel restores focus to the button it was opened
            // from in its own unmount cleanup, which is a passive effect and therefore lands
            // BEFORE a rAF — so a single frame would already be enough, and the second is the
            // margin for a reveal that scrolls or expands on its way. Focus is not moved when
            // the host never attached the ref (S2-24).
            requestAnimationFrame(() => requestAnimationFrame(() => element.current?.focus()));
        } : undefined),
        [id, canReveal, registerAnchor],
    );

    return {
        isNew: isAnchorActive(id),
        markUsed: () => markAnchorUsed(id),
        spotlight: isSpotlit(id),
        ref: element,
    };
}

/**
 * What "Show me" leaves on the element for a moment.
 *
 * Exported as a constant rather than described in prose so that every app that copies this
 * folder highlights the same way, and so that the ring uses the app's focus token (`--focus`,
 * 6.57:1 on white) instead of whatever the next caller reaches for. It was `ring-blue-500` on
 * arrival, where `blue-500` is the origin app's green and this app's blue.
 */
export const SPOTLIGHT_CLASS = 'ring-2 ring-focus ring-offset-2 rounded-md';
